import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../ui/carousel';
import { followingsService, type FollowingItem } from '../../services/followings';

interface FollowingCarouselProps {
  onEmpty?: () => void;
}

export default function FollowingCarousel({ onEmpty }: FollowingCarouselProps) {
  const [followings, setFollowings] = useState<FollowingItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const loadedPagesRef = useRef(new Set<number>());
  const isLoadingRef = useRef(false);
  const navigate = useNavigate();

  const loadPage = useCallback(async (page: number) => {
    if (loadedPagesRef.current.has(page) || isLoadingRef.current) return;

    isLoadingRef.current = true;
    setIsLoading(true);
    try {
      const response = await followingsService.getFollowings(page);
      if (response?.data && Array.isArray(response.data)) {
        setFollowings((prev) => [...prev, ...response.data]);
        setHasMore(response.meta?.has_more ?? false);
        loadedPagesRef.current.add(page);
      }
    } catch (error) {
      console.error('Failed to load followings:', error);
      setHasMore(false);
    } finally {
      isLoadingRef.current = false;
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleItemClick = (item: FollowingItem) => {
    if (item.type === 'gym') {
      navigate(`/gyms/${item.attributes.slug}`);
    } else if (item.type === 'crag') {
      navigate(`/crags/${item.attributes.slug}`);
    } else if (item.type === 'competition') {
      navigate(`/competitions/${item.attributes.slug}`);
    } else {
      navigate(`/users/${item.attributes.slug}`);
    }
  };

  const handleScroll = useCallback(() => {
    if (hasMore && !isLoadingRef.current) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      loadPage(nextPage);
    }
  }, [hasMore, currentPage, loadPage]);

  const getDisplayName = (item: FollowingItem): string => {
    if (item.type !== 'user') {
      return item.attributes.name || '';
    }
    return item.attributes.username || '';
  };

  const getInitial = (name: string): string => {
    return name.charAt(0).toUpperCase();
  };

  useEffect(() => {
    if (!isLoading && followings.length === 0) {
      onEmpty?.();
    }
  }, [isLoading, followings.length, onEmpty]);

  if (followings.length === 0 && !isLoading) {
    return null; // Don't show carousel if no followings
  }

  return (
    <div className="w-full mb-6">
      <Carousel
        opts={{
          align: 'start',
          loop: false,
        }}
        className="w-full"
      >
        <CarouselContent className="-ml-2 md:-ml-4">
          {followings.map((item, index) => {
            const displayName = getDisplayName(item);
            const isNearEnd = index >= followings.length - 5;

            // Trigger lazy load when approaching the end
            if (isNearEnd && hasMore && !isLoading && index === followings.length - 3) {
              handleScroll();
            }

            return (
              <CarouselItem key={`${item.type}-${item.id}`} className="basis-auto pl-2 md:pl-4">
                <div
                  className="flex flex-col items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => handleItemClick(item)}
                >
                  <div className="relative p-[2px] rounded-full bg-gradient-to-br">
                    <Avatar className="h-16 w-16 border-2 border-background">
                      <AvatarImage
                        src={item.attributes.avatar_url || undefined}
                        alt={displayName}
                        loading="lazy"
                      />
                      <AvatarFallback>
                        {getInitial(displayName)}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <span className="text-xs font-medium text-center max-w-[80px] truncate">
                    {displayName}
                  </span>
                </div>
              </CarouselItem>
            );
          })}
          {isLoading && (
            <CarouselItem className="basis-auto pl-2 md:pl-4">
              <div className="flex flex-col items-center gap-2">
                <div className="h-16 w-16 rounded-full bg-muted animate-pulse" />
                <span className="text-xs text-muted-foreground">Loading...</span>
              </div>
            </CarouselItem>
          )}
        </CarouselContent>
        <CarouselPrevious />
        <CarouselNext />
      </Carousel>
    </div>
  );
}
